import { Order } from 'core/order/Order';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import i18n from 'i18n';
import { Trans } from 'react-i18next';
import styles from './modal.module.scss';
import { useCallAPI } from 'hooks/useCallAPI';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { DateRangePickerField } from 'components/form/field/DateRangePickerField';
import moment from 'moment';
import { DAILY_BUDGET_STATE } from 'containers/RtbCampaigns/RtbCampaignSetupFlow/RtbCampaignForm/RtbCampaignBasicFormModel';
import { DefaultRtbCampaignManager } from 'core/rtbCampaign/RtbCampaignManager';
import { AlertDialog } from 'components/AlertDialog';
import { L1Object } from 'core/l1Object/L1Object';
import { Modal } from 'components/Modal/Modal';
import { validateCampaignDateRange } from 'utils/CampaignValidators';

const campaignManager = new DefaultRtbCampaignManager();

export const renderModifyDurationModal = (
  campaign: {
    id: number;
    budget: number;
    dailyTargetBudget: number | undefined;
    startDate: string;
    endDate: string;
  },
  order: Order,
  l1Object: L1Object,
  onClose: (needRefreshList: boolean) => void
) => {
  return (
    <ModifyDuration
      campaign={campaign}
      order={order}
      l1Object={l1Object}
      onClose={onClose}
    />
  );
};

export const ModifyDuration: React.FC<{
  campaign: {
    id: number;
    budget: number;
    dailyTargetBudget: number | undefined;
    startDate: string;
    endDate: string;
  };
  order: Order;
  l1Object: L1Object;
  onClose: (needRefreshList: boolean) => void;
}> = ({
  campaign,
  order,
  l1Object,
  onClose
}) => {

  const { id, budget, dailyTargetBudget, startDate, endDate } = campaign;

  const [showFinalCheckModal, setShowFinalCheckModal] = useState<boolean>(false);

  const { loading, callAPIs } = useCallAPI();

  const [alertContent, setAlertContent] = useState<string | ReactElement | ReactElement[]>();

  const [newStartDate, setNewStartDate] = useState<string>(startDate);
  const [newEndDate, setNewEndDate] = useState<string>(endDate);

  const dailyBudgetState = useMemo(() => {
    return campaignManager.getCampaignDailyBudgetState(
      budget,
      dailyTargetBudget,
      newStartDate,
      newEndDate
    );
  }, [budget, dailyTargetBudget, newStartDate, newEndDate]);

  const minDate = useMemo(() => {
    const orderStartDate = moment(order.startDate);
    const thisHour = moment().startOf('hour').format('YYYY-MM-DD_HH:mm:ss');
    return moment(startDate).isBefore(orderStartDate)
      ? moment().isAfter(orderStartDate)
        ? thisHour
        : orderStartDate.format('YYYY-MM-DD_HH:mm:ss')
      : moment(startDate).isBefore()
        ? moment(startDate).format('YYYY-MM-DD_HH:mm:ss')
        : thisHour;
  }, [order.startDate, startDate]);

  const maxDate = useMemo(() => {
    return moment(order.endDate).endOf('day').format('YYYY-MM-DD_HH:mm:ss');
  }, [order.endDate]);

  const isCampaignStart = moment(startDate).isBefore(moment());

  const checkIsUnderBudget = useCallback(() => {
    if (dailyBudgetState === DAILY_BUDGET_STATE.UNDER_BUDGET) {
      setAlertContent(
        <Trans i18nKey='campaign.descriptions.underDailyBudget'>
          ...<span className='text-danger'>...</span>...
        </Trans>
      );
    }
  }, [dailyBudgetState]);

  const dateRangeError = useMemo(() => {
    return validateCampaignDateRange({
      startDate: newStartDate,
      endDate: newEndDate,
      orderStartDate: order.startDate,
      orderEndDate: order.endDate,
      defaultStartDate: startDate,
      defaultEndDate: endDate,
      isNewCampaign: false
    });
  }, [startDate, endDate, newStartDate, newEndDate, order.endDate, order.startDate]);

  const renderCampaignScheduling = useCallback(() => {
    const endDateMoment = moment(newEndDate);
    const startDateMoment = moment(newStartDate);
    const days = endDateMoment.diff(startDateMoment, 'days') + 1;
    const hours = endDateMoment.diff(startDateMoment, 'hours') + 1;
    if (days > 1) {
      return (
        <Trans i18nKey='campaign.descriptions.campaignSchedulingDay'>
          Total <span className='text-dark'>{{ days }} days</span>
        </Trans>
      );
    } else if (days === 1) {
      return (
        <Trans i18nKey='campaign.descriptions.campaignSchedulingHour'>
          Total <span className='text-dark'>{{ hours }} hours</span>
        </Trans>
      );
    }
  }, [newStartDate, newEndDate]);

  const onChangeSchedule = useCallback((newStartDate: string | undefined, newEndDate: string | undefined) => {
    newStartDate && setNewStartDate(newStartDate);
    newEndDate && setNewEndDate(newEndDate);
  }, []);

  const renderDuration = useCallback(() => {
    return (
      <DateRangePickerField
        label={i18n.t<string>('campaignInfo.labels.dayRange')}
        minDate={minDate}
        maxDate={maxDate}
        startDate={newStartDate}
        endDate={newEndDate}
        name='dayRange'
        timeInterval={60}
        startDatePickerDisabled={isCampaignStart}
        onPopOverClosed={checkIsUnderBudget}
        onChange={onChangeSchedule}
        permanentHint={<span className={styles.permanentHint}>{i18n.t<string>('campaignInfo.labels.dateCannotModifyHint')}</span>}
        hint={renderCampaignScheduling()}
        fieldContentWidth={400}
        error={dateRangeError}
      />
    );
  }, [
    newStartDate,
    newEndDate,
    minDate,
    maxDate,
    isCampaignStart,
    dateRangeError,
    checkIsUnderBudget,
    onChangeSchedule,
    renderCampaignScheduling
  ]);

  const onConfirmButtonClick = useCallback(() => {
    setShowFinalCheckModal(true);
  }, []);

  const onCloseFinalCheckModal = useCallback(() => {
    setShowFinalCheckModal(false);
  }, []);

  const leaveModifyBudgetModal = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const dismissAlertWindow = useCallback(() => {
    setAlertContent(undefined);
  }, []);

  const modify = useCallback(() => {
    if (newStartDate === startDate && newEndDate === endDate) {
      onClose(false);
      return;
    }
    callAPIs(
      [
        () =>
          campaignManager.editCampaignDuration(
            l1Object.l1ObjectId,
            id,
            newStartDate,
            newEndDate
          )
      ],
      () => {
        onClose(true);
      }
    );
  }, [
    id,
    l1Object.l1ObjectId,
    startDate,
    newStartDate,
    endDate,
    newEndDate,
    onClose,
    callAPIs
  ]);

  const startDateChanged = newStartDate !== startDate;
  const endDateChanged = newEndDate !== endDate;

  return (
    <Modal
      title=''
      secondaryButton={{
        title: i18n.t<string>('common.buttons.cancel'),
        callback: leaveModifyBudgetModal
      }}
      primaryButton={{
        title: i18n.t<string>('common.buttons.confirm'),
        callback: onConfirmButtonClick,
        disabled: !!dateRangeError || (!startDateChanged && !endDateChanged)
      }}
      dismiss={leaveModifyBudgetModal}
      className={styles.modal}
      backdrop='static'
    >
      {loading && <LoadingIndicator />}
      {renderDuration()}
      {alertContent && (
        <AlertDialog
          title={i18n.t<string>('common.warning')}
          message={alertContent}
          danger={false}
          dismiss={dismissAlertWindow}
        />
      )}
      {showFinalCheckModal &&
        <Modal
          title={i18n.t<string>('modifyDuration.titles.confirmModifyDuration')}
          dismiss={onCloseFinalCheckModal}
          className={styles.confirmModal}
          primaryButton={{ title: i18n.t<string>('common.buttons.confirm'), callback: modify }}
          secondaryButton={{ title: i18n.t<string>('common.buttons.cancel'), callback: onCloseFinalCheckModal }}
        >
          <Trans i18nKey='modifyDuration.labels.confirmModifyDuration'>
            ...<span className={styles.danger}>{{ value: `${newStartDate} ~ ${newEndDate}` }}</span>...
          </Trans>
        </Modal>
      }
    </Modal>
  );
};
